import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import styles from './PoPage.module.css';
import { PageShell, type PageShellRender } from '@/components/PageShell';
import { KpiStrip, type Kpi } from '@/components/KpiStrip';
import { Panel, SegmentedControl } from '@/components/Panel';
import { MetricTable, Tag, type MetricColumn } from '@/components/MetricTable';
import { DataSourceBanner } from '@/features/availability/DataSourceBanner';
import {
  fetchPo, type PoLevelRow, type PoOverview, type PurchaseOrder,
} from '@/lib/pages.api';
import { deltaPct, deltaPoints, inr, pctOf, plain, shortDate, tone, units } from '@/lib/money';
import { titleCase } from '@/lib/format';

/**
 * PO Modules — Availability > PO Modules.
 *
 * Two rules from the knowledge base shape everything on this page, and both are
 * about not saying something false confidently:
 *
 *  FILL RATE IS AGGREGATED, NEVER AVERAGED — delivered units over ordered
 *  units across the closed, uncensored lines. The page states the line count it
 *  judged, so the figure can be read as evidence rather than as a score.
 *
 *  po_state IS A DISPLAY STATE, NOT AN OUTCOME. Three quarters of the lines
 *  Blinkit marks 'Expired' were delivered in full. The list therefore shows the
 *  portal's own state AND the lifecycle the warehouse derived, side by side,
 *  and never treats the first as a verdict.
 *
 * An open PO also shows PROGRESS rather than fill rate. Fill rate is judged on
 * closed lines only, so a PO whose closed lines all delivered in full reads
 * 100% while units are still outstanding — true, and unreadable next to a
 * non-zero outstanding column.
 */

const LEVEL_LABEL = {
  city: 'City',
  facility: 'Facility',
  vendor: 'Vendor',
  item: 'Item',
} as const;
type Level = keyof typeof LEVEL_LABEL;

export function PoPage({
  onNavigate,
  onSignOut,
}: {
  onNavigate?: (section: string, page: string) => void;
  onSignOut?: () => void;
}) {
  const [resolved, setResolved] = useState<PoOverview['window'] | undefined>(undefined);
  return (
    <PageShell
      title="PO Modules"
      activeNavId="po-modules"
      activeSection="availability"
      chipKinds={['city']}
      {...(onNavigate ? { onNavigate } : {})}
      {...(onSignOut ? { onSignOut } : {})}
      resolved={resolved}
    >
      {(ctx) => <PoBody ctx={ctx} onResolved={setResolved} />}
    </PageShell>
  );
}

function PoBody({
  ctx,
  onResolved,
}: {
  ctx: PageShellRender;
  onResolved: (w: PoOverview['window']) => void;
}) {
  const [level, setLevel] = useState<Level>('city');
  const [basis, setBasis] = useState<'order_date' | 'close_date'>('order_date');
  const [onlyOpen, setOnlyOpen] = useState(true);

  const params = useMemo(
    () => ({
      customer_name: ctx.customer,
      window: ctx.range,
      filters: ctx.scope.city ? { city: ctx.scope.city } : {},
      date_basis: basis,
      only_open: onlyOpen,
      po_limit: 60,
    }),
    [ctx.customer, ctx.range, ctx.scope.city, basis, onlyOpen],
  );

  const query = useQuery({
    queryKey: ['po-overview', params],
    queryFn: ({ signal }) => fetchPo(params, signal),
    enabled: ctx.customer !== '',
    staleTime: 5 * 60 * 1000,
    placeholderData: (prev) => prev,
  });

  const data = query.data;
  const win = data?.window;
  useEffect(() => {
    if (win) onResolved(win);
  }, [win, onResolved]);
  const loading = query.isPending && !data;

  const kpis: Kpi[] = useMemo(() => {
    const k = data?.kpis;
    return [
      {
        key: 'fill_rate',
        label: 'Fill rate',
        value: pctOf(k?.fill_rate.value ?? null),
        delta: deltaPoints(k?.fill_rate.delta),
        tone: tone(k?.fill_rate.delta),
        help: data?.response_meta.fill_rate_note,
        emphasis: true,
      },
      {
        key: 'open_po',
        label: 'Open POs',
        value: plain(k?.open_po_count.value ?? null),
        foot: 'state as of now — no period comparison',
        emphasis: true,
      },
      {
        key: 'open_value',
        label: 'Open, undelivered',
        value: inr(k?.open_undelivered_value.value ?? null),
      },
      {
        key: 'expiring',
        label: `Expiring soon`,
        value: plain(k?.expiring_soon_count.value ?? null),
        foot: inr(k?.expiring_soon_value.value ?? null),
        help: 'Open POs whose expiry has passed or falls inside the horizon. Overdue ones are included — an open PO past its expiry is not less urgent than one expiring tomorrow.',
      },
      {
        key: 'unscheduled',
        label: 'Unscheduled value',
        value: inr(k?.unscheduled_open_value.value ?? null),
        foot: 'open, nothing booked',
      },
      {
        key: 'ordered',
        label: 'Ordered',
        value: inr(k?.ordered_value.value ?? null),
        delta: deltaPct(k?.ordered_value.delta_pct),
        tone: tone(k?.ordered_value.delta_pct),
      },
      {
        key: 'delivered',
        label: 'Delivered',
        value: inr(k?.delivered_value.value ?? null),
        delta: deltaPct(k?.delivered_value.delta_pct),
        tone: tone(k?.delivered_value.delta_pct),
      },
      {
        key: 'unfulfilled',
        label: 'Unfulfilled lines',
        value: plain(k?.unfulfilled_lines.value ?? null),
        delta: deltaPct(k?.unfulfilled_lines.delta_pct),
        tone: tone(k?.unfulfilled_lines.delta_pct, true),
        help: 'Lines where nothing at all was delivered. Taken from delivered quantity, never from the portal’s display state.',
      },
      {
        key: 'censored',
        label: 'Not judged',
        value: plain(k?.censored_lines.value ?? null),
        foot: 'aged out still open',
        help: 'Lines that left the 30-day source window before closing. They have no outcome, so they are excluded from the fill rate rather than counted as misses.',
      },
    ];
  }, [data]);

  const levelColumns: MetricColumn<PoLevelRow>[] = useMemo(
    () => [
      {
        key: 'label',
        header: LEVEL_LABEL[level],
        align: 'left',
        width: '26%',
        render: (r) => titleCase(r.label) ?? r.label,
        sub: (r) => (r.extra ? { text: titleCase(r.extra) ?? r.extra } : undefined),
      },
      {
        key: 'fill', sortValue: (r) => r.fill_rate ?? null,
        header: 'Fill rate',
        render: (r) => pctOf(r.fill_rate),
        sub: (r) =>
          r.unfulfilled_lines != null && r.unfulfilled_lines > 0
            ? { text: `${plain(r.unfulfilled_lines)} unfulfilled`, tone: 'down' }
            : undefined,
      },
      { key: 'ordered', sortValue: (r) => r.ordered_value ?? null, header: 'Ordered', render: (r) => inr(r.ordered_value) },
      { key: 'delivered', sortValue: (r) => r.delivered_value ?? null, header: 'Delivered', render: (r) => inr(r.delivered_value) },
      { key: 'undelivered', sortValue: (r) => r.undelivered_value ?? null, header: 'Undelivered', render: (r) => inr(r.undelivered_value) },
      {
        key: 'open', sortValue: (r) => r.open_po_count ?? null,
        header: 'Open POs',
        render: (r) => plain(r.open_po_count),
        sub: (r) => ({ text: `${inr(r.open_undelivered_value)} open` }),
      },
      {
        key: 'expiry', sortValue: (p) => p.next_expiry_open ?? null,
        header: 'Next expiry',
        render: (r) => shortDate(r.next_expiry_open),
      },
      {
        key: 'amended', sortValue: (r) => r.amended_lines ?? null,
        header: 'Amended',
        render: (r) => plain(r.amended_lines),
      },
      {
        key: 'censored', sortValue: (r) => r.censored_lines ?? null,
        header: 'Not judged',
        render: (r) => plain(r.censored_lines),
      },
    ],
    [level],
  );

  const poColumns: MetricColumn<PurchaseOrder>[] = [
    {
      key: 'po',
      header: 'PO',
      align: 'left',
      width: '18%',
      render: (p) => String(p.po_number),
      sub: (p) => ({
        text: [titleCase(p.facility_name ?? '') , titleCase(p.city ?? '')].filter(Boolean).join(' · '),
      }),
    },
    {
      key: 'state',
      header: 'State',
      align: 'left',
      help: 'Left: the portal’s own display state. Right: the lifecycle the warehouse derived. They disagree by design — three quarters of "Expired" lines were delivered in full.',
      render: (p) => (
        <span className={styles.states}>
          <Tag>{p.po_state ?? '—'}</Tag>
          <Tag tone={p.po_lifecycle_status === 'Open' ? 'warn' : 'good'}>
            {p.po_lifecycle_status ?? '—'}
          </Tag>
        </span>
      ),
    },
    {
      key: 'expiry',
      header: 'Expiry',
      render: (p) => shortDate(p.expiry_date),
      sub: (p) => {
        if (p.days_to_expiry == null) return undefined;
        if (p.days_to_expiry < 0) {
          return { text: `${Math.abs(p.days_to_expiry)}d overdue`, tone: 'down' };
        }
        return { text: `in ${p.days_to_expiry}d`, tone: p.days_to_expiry <= 3 ? 'down' : undefined };
      },
    },
    {
      key: 'appt', sortValue: (p) => p.appointment_date ?? null,
      header: 'Appointment',
      render: (p) =>
        p.appointment_date ? shortDate(p.appointment_date) : <span className={styles.warn}>none</span>,
      sub: (p) =>
        p.amendments.appointment ? { text: `${p.amendments.appointment} amendments` } : undefined,
    },
    { key: 'ordered', sortValue: (p) => p.units_ordered ?? null, header: 'Ordered', render: (p) => units(p.units_ordered) },
    { key: 'delivered', sortValue: (p) => p.delivered_quantity ?? null, header: 'Delivered', render: (p) => units(p.delivered_quantity) },
    {
      key: 'progress', sortValue: (p) => p.delivered_pct ?? null,
      header: 'Progress',
      help: 'Delivered over ordered across every line. This is NOT the fill rate: fill rate is judged on closed lines only, so an open PO can read 100% there while units are still outstanding here.',
      render: (p) => pctOf(p.delivered_pct),
      sub: (p) =>
        p.po_lifecycle_status === 'Closed' && p.fill_rate != null
          ? { text: `fill ${pctOf(p.fill_rate)}` }
          : undefined,
    },
    { key: 'value', sortValue: (p) => p.undelivered_value ?? null, header: 'Undelivered', render: (p) => inr(p.undelivered_value) },
  ];

  return (
    <>
      {query.isError && (
        <DataSourceBanner kind="error" detail={query.error.message} />
      )}

      <KpiStrip items={kpis} loading={loading} />

      <Panel
        title="Fulfilment by level"
        caption={data?.response_meta.basis_note}
        actions={
          <div className={styles.controls}>
            <SegmentedControl
              label="Date basis"
              value={basis}
              onChange={setBasis}
              options={[
                { value: 'order_date', label: 'By order date' },
                { value: 'close_date', label: 'By close date' },
              ]}
            />
            <SegmentedControl
              label="Breakdown level"
              value={level}
              onChange={setLevel}
              options={[
                { value: 'city', label: 'City' },
                { value: 'facility', label: 'Facility' },
                { value: 'vendor', label: 'Vendor' },
                { value: 'item', label: 'Item' },
              ]}
            />
          </div>
        }
        footnote={data?.response_meta.fill_rate_note}
        dense
      >
        <MetricTable
          searchable
          caption={`Fill rate by ${LEVEL_LABEL[level]}`}
          columns={levelColumns}
          rows={data?.levels[level].rows ?? []}
          rowKey={(r) => r.id}
          loading={loading}
          empty={`No purchase orders at ${LEVEL_LABEL[level].toLowerCase()} level in this window.`}
        />
      </Panel>

      <Panel
        title="Purchase orders"
        caption="Ordered by how little time is left, then by what is still owed — the working order, not the reporting order."
        actions={
          <SegmentedControl
            label="Which POs"
            value={onlyOpen ? 'open' : 'all'}
            onChange={(v) => { setOnlyOpen(v === 'open'); }}
            options={[
              { value: 'open', label: 'Open only' },
              { value: 'all', label: 'All' },
            ]}
          />
        }
        dense
      >
        <MetricTable
          searchable
          caption="Purchase orders"
          columns={poColumns}
          rows={data?.purchase_orders ?? []}
          rowKey={(p) => String(p.po_number)}
          loading={loading}
          skeletonRows={8}
          empty={onlyOpen ? 'No open purchase orders in this window.' : 'No purchase orders in this window.'}
        />
      </Panel>
    </>
  );
}
